import type { ErrorEvent, Exception } from "@sentry/browser";

import { isUnhandled } from "./sentry_event";

// Failures the browser raises when it takes a resource away from work the page
// still had in flight: a request cut off because the person navigated away or
// closed the tab, and a storage handle the engine reclaimed underneath us.
//
// Nothing in the page is broken when one of these arrives — the session that
// produced it had already ended, or the browser had already decided to spend the
// memory elsewhere — and none of them leaves a stack of ours to act on.
//
// Written as the DOMException's canonical `Name: message`, because that is the
// one form every arrival shape below can be matched against. Matched whole for
// the reason the bare-fetch list is: "the operation was aborted." is a substring
// of wordings that name a real failure, and a drop that spreads on its own is
// the one thing these filters must never do.
const BROWSER_TEARDOWN_MESSAGES = [
  // Chromium, for a request the page abandoned by going away. PARTNERS-WEB-6V0.
  "aborterror: the connection was closed.",
  // WebKit's wording for the same abandonment. PARTNERS-WEB-66F.
  "aborterror: the operation was aborted.",
  // WebKit reclaiming the IndexedDB connection out from under an open database.
  // The message's own advice — reload — is what the person has already done by
  // the time we could read it. PARTNERS-WEB-65X.
  "unknownerror: connection to indexed database server lost. refresh the page to try again",
  // The same reclaim raised against the closing handle rather than the lost
  // server, in the wording that names the failing call. PARTNERS-WEB-702.
  "invalidstateerror: failed to execute 'transaction' on 'idbdatabase': the database connection is closing.",
  // iOS reclaiming backing storage mid-read, under memory pressure or when the
  // tab is backgrounded. PARTNERS-WEB-6T2.
  "notreadableerror: the i/o read operation failed.",
];

/**
 * Whether one reported exception is a teardown, under any of the shapes the
 * same DOMException arrives in.
 *
 * Where the name sits depends on how the exception reached the handler, and one
 * failure reaches us every way. Thrown, it keeps its own name as the exception
 * type and its bare message as the value. Rejected, the SDK cannot read a stack
 * off it and rebuilds it as a plain Error whose *message* is already
 * `Name: message` — and a wrapper that rethrows one produces that same shape
 * under a type of its own (PARTNERS-WEB-64J carries the storage teardown above
 * as a TypeError). So rather than deciding which shape this is, both candidate
 * forms are tested against the canonical list: the value alone for the shapes
 * that already carry the name, and `type: value` for the one that does not.
 *
 * Matching one shape alone would filter a failure on Safari and leave the
 * identical one reporting from Chrome, which then reads as a browser-specific
 * fault rather than the engine-agnostic teardown it is.
 */
function isTeardownException(exception: Exception): boolean {
  const message = (exception.value ?? "").trim().toLowerCase();
  const name = (exception.type ?? "").trim().toLowerCase();

  return (
    BROWSER_TEARDOWN_MESSAGES.includes(message) ||
    BROWSER_TEARDOWN_MESSAGES.includes(`${name}: ${message}`)
  );
}

/**
 * Whether the browser discarded work the page had in flight, rather than the
 * page failing at it.
 *
 * Gated on nobody having caught it. These are ordinary DOMExceptions, and our
 * own code can legitimately await something that aborts — a fetch we abandon on
 * unmount, a storage read we fall back from — and report it deliberately with
 * the context that makes it actionable. That capture is the one thing here worth
 * keeping, and being caught is all that separates it from the noise, exactly as
 * it is for the in-app browser drop.
 *
 * Judged on the thrown exception alone — the last of the reported values, which
 * is the convention the SDK itself reads a report's identity by. An error chained
 * onto a cause is unwrapped into earlier values, so testing them all would let a
 * teardown buried in the cause of a wider failure drop the whole report: the
 * same inheritance the whole-message match above exists to prevent, arriving by
 * another route.
 */
export function isBrowserTeardownError(event: ErrorEvent): boolean {
  if (!isUnhandled(event)) {
    return false;
  }

  const values = event.exception?.values ?? [];

  return values.length > 0 && isTeardownException(values[values.length - 1]);
}
